import React from 'react';
import { DeepMap, UseFormMethods } from 'react-hook-form';
import {
  FormInputTypes,
  getUpdateInputValueFunction,
  RadioGroupFormOption,
  TypedFormInputs,
} from 'components/form';
import {
  ScheduleWorker,
  JobsiteScheduledWorkerFormFields,
} from 'containers/jobsite/tabs/jobsiteSchedules/data/types';
import { cn, ensureNonUndefinedFields, getAsNumber } from 'utils';
import { SelectOptionElement, useDidUpdateEffect } from '@odin-labs/components';
import { getSelectOptions } from 'components/utils';
import {
  CreateScheduleWorkerInput,
  ScheduleWorkerType,
  UpdateScheduleWorkerInput,
} from 'apollo/generated/client-operations';
import {
  JobsiteContractorOption,
  useJobsiteContractorWorkersOptions,
} from 'containers/utils';
import { RequiredField } from 'utils/validation';

export function useSelectOptions(args: {
  values: string[];
  extraValue?: string;
}): SelectOptionElement[] {
  const { values, extraValue } = args;

  return React.useMemo(() => {
    return (
      getSelectOptions({
        values: (extraValue && !values.includes(extraValue)
          ? [extraValue, ...values]
          : values
        )?.sort(),
        keyGetter: null,
        labelGetter: null,
      }) ?? []
    );
  }, [values, extraValue]);
}

export function useSelectOptionsWithExtraValues(args: {
  options: SelectOptionElement[];
  extraValue?: string;
}): SelectOptionElement[] {
  const { options, extraValue } = args;
  return React.useMemo((): SelectOptionElement[] => {
    // If no extraValue is provided or it already exists in the options, return the options as is
    if (!extraValue || options?.some((option) => option.value === extraValue)) {
      return options;
    }
    return [...options, { value: extraValue, label: extraValue }].sort((a, b) =>
      a.label.localeCompare(b.label),
    );
  }, [options, extraValue]);
}

export type ScheduleWorkerTypeOption = RadioGroupFormOption<
  Pick<JobsiteScheduledWorkerFormFields, 'jobsiteWorkerId' | 'quantity'>,
  ScheduleWorkerType
>;

export const getScheduleWorkerTypeOptions = ({
  jobsiteWorkersOptions,
  selectedTypeValue,
}: {
  jobsiteWorkersOptions: SelectOptionElement[];
  selectedTypeValue: ScheduleWorkerType;
}): ScheduleWorkerTypeOption[] => {
  return [
    {
      value: ScheduleWorkerType.Scheduled,
      label: 'A number of workers',
      inputsContainerLayout: cn('odin-flex'),
      inputsVisibility: 'always',
      inputsAvailability: 'whenChecked',
      inputsPosition: 'inline',
      inputs: [
        {
          name: 'quantity',
          element: FormInputTypes.Field,
          elementProps: {
            fieldType: 'number',
          },
          validation: {
            required: {
              value: selectedTypeValue === ScheduleWorkerType.Scheduled,
              message: RequiredField`Number of workers`,
            },
            min: {
              value: 1,
              message: 'Number of workers should be greater than 0',
            },
          },
          layout: '', // cn('sm:odin-w-1/2'),
        },
      ],
    },
    {
      value: ScheduleWorkerType.Assigned,
      label: 'A specific worker',
      inputsContainerLayout: cn('odin-flex'),
      inputsVisibility: 'always',
      inputsAvailability: 'whenChecked',
      inputs: [
        {
          name: 'jobsiteWorkerId',
          element: FormInputTypes.Select,
          elementProps: {
            placeholder: 'Select worker',
            options: jobsiteWorkersOptions,
            clearToNull: true,
          },
          validation: {
            required: {
              value: selectedTypeValue === ScheduleWorkerType.Assigned,
              message: RequiredField`Worker`,
            },
          },
          layout: cn('odin-w-full'), // cn('sm:odin-w-1/2'),
        },
      ],
    },
  ];
};

function getFormInputs(args: {
  jobsiteContractorsOptions: SelectOptionElement[];
  scheduleWorkerTypeOptions: ScheduleWorkerTypeOption[];
  tradeOptions: SelectOptionElement[];
  activityCodeOptions: SelectOptionElement[];
  selectedTypeValue?: ScheduleWorkerType;
  workerHasTrade?: boolean;
}): TypedFormInputs<JobsiteScheduledWorkerFormFields> {
  const {
    jobsiteContractorsOptions,
    scheduleWorkerTypeOptions,
    tradeOptions,
    activityCodeOptions,
    selectedTypeValue,
    workerHasTrade,
  } = args;
  return {
    jobsiteContractorId: {
      element: FormInputTypes.Select,
      label: 'Contractor',
      elementProps: {
        options: jobsiteContractorsOptions,
        clearToNull: true,
        autoFocus: true,
      },
      validation: { required: true },
      layout: '',
    },
    type: {
      element: FormInputTypes.RadioGroup,
      label: 'Choose which type of worker to schedule',
      elementProps: {
        options: scheduleWorkerTypeOptions,
        preventSubmitOnEnter: true,
      },
      validation: { required: true },
      layout: '',
    },
    trade: {
      element: FormInputTypes.Select,
      label: 'Trade',
      elementProps: {
        options: tradeOptions,
        disabled:
          selectedTypeValue === ScheduleWorkerType.Assigned && !!workerHasTrade,
      },
      validation: { required: true },
      layout: '',
    },
    activity: {
      element: FormInputTypes.Select,
      label: 'Activity',
      elementProps: {
        options: activityCodeOptions,
      },
      validation: { required: true },
      layout: '',
    },
    notes: {
      element: FormInputTypes.TextAreaField,
      label: 'Notes',
      layout: '',
    },
  };
}

export const getFormInputsHook =
  ({
    jobsiteOrganizationId,
    jobsiteContractorsOptions,
    tradeOptions,
    activityCodeOptions,
  }: {
    jobsiteOrganizationId: string;
    jobsiteContractorsOptions: JobsiteContractorOption[];
    tradeOptions: SelectOptionElement[];
    activityCodeOptions: SelectOptionElement[];
  }) =>
  (
    form: UseFormMethods<JobsiteScheduledWorkerFormFields>,
  ): TypedFormInputs<JobsiteScheduledWorkerFormFields> => {
    const { watch, getValues, setValue } = form;
    const { isSubmitted } = form.formState;

    const selectedJobsiteContractor = watch('jobsiteContractorId');

    const selectedJobsiteContractorId =
      selectedJobsiteContractor && selectedJobsiteContractor.value;
    const selectedJobsiteContractorOrganizationId =
      selectedJobsiteContractor?.organizationId;

    const jobsiteWorkersOptions = useJobsiteContractorWorkersOptions({
      jobsiteContractorId: selectedJobsiteContractorId,
    });

    const selectedType = watch('type');
    const selectedTypeValue = selectedType?.value;

    const selectedJobsiteWorkerOption = watch('jobsiteWorkerId');

    const scheduleWorkerTypeOptions = React.useMemo(() => {
      return getScheduleWorkerTypeOptions({
        jobsiteWorkersOptions,
        selectedTypeValue,
      });
    }, [jobsiteWorkersOptions, selectedTypeValue]);

    // Reset `jobsiteWorkerId` when jobsiteContractorId changes
    // and set `type` to `Assigned` if the selected contractor is from the same organization
    // or `Scheduled` if it is from a different organization
    useDidUpdateEffect(() => {
      if (getValues('jobsiteWorkerId')) {
        setValue('jobsiteWorkerId', null, {
          shouldDirty: true,
          shouldValidate: isSubmitted,
        });
      }
      const newType =
        selectedJobsiteContractorId &&
        (selectedJobsiteContractorOrganizationId === jobsiteOrganizationId
          ? ScheduleWorkerType.Assigned
          : ScheduleWorkerType.Scheduled);
      if (newType !== getValues('type')?.value) {
        setValue(
          'type',
          scheduleWorkerTypeOptions.find((option) => option.value === newType),
          { shouldDirty: true, shouldValidate: isSubmitted },
        );
      }
    }, [selectedJobsiteContractorId]);

    // Reset `quantity` to 1 when `type` changes to `Assigned`
    // or reset `jobsiteWorkerId` when `type` changes to `Scheduled`
    useDidUpdateEffect(() => {
      if (!selectedTypeValue) return;
      if (selectedTypeValue === ScheduleWorkerType.Assigned) {
        setValue('quantity', 1, {
          shouldDirty: true,
          shouldValidate: isSubmitted,
        });
      } else {
        setValue('jobsiteWorkerId', null, {
          shouldDirty: true,
          shouldValidate: isSubmitted,
        });
      }
    }, [selectedTypeValue]);

    // Set `trade` based on the selected worker's trade
    // when `type` is `Assigned` and a worker is selected
    const workerTradeValue = selectedJobsiteWorkerOption?.trade;
    const newTradeOptions = useSelectOptionsWithExtraValues({
      options: tradeOptions,
      extraValue: workerTradeValue,
    });

    useDidUpdateEffect(() => {
      if (selectedTypeValue !== ScheduleWorkerType.Assigned) return;

      if (workerTradeValue) {
        const tradeOption =
          newTradeOptions.find((t) => t.value === workerTradeValue) ?? null;

        setValue('trade', tradeOption, {
          shouldDirty: true,
          shouldValidate: isSubmitted,
        });
      }
    }, [selectedTypeValue, workerTradeValue, newTradeOptions]);

    // Reset `trade` when `type` changes to `Scheduled`
    useDidUpdateEffect(() => {
      if (selectedTypeValue === ScheduleWorkerType.Scheduled) {
        setValue('trade', null, {
          shouldDirty: true,
          shouldValidate: isSubmitted,
        });
      }
    }, [selectedTypeValue]);

    return React.useMemo(() => {
      return getFormInputs({
        jobsiteContractorsOptions,
        scheduleWorkerTypeOptions,
        tradeOptions: newTradeOptions,
        activityCodeOptions,
        selectedTypeValue,
        workerHasTrade: Boolean(workerTradeValue),
      });
    }, [
      jobsiteContractorsOptions,
      scheduleWorkerTypeOptions,
      newTradeOptions,
      activityCodeOptions,
      selectedTypeValue,
      workerTradeValue,
    ]);
  };

export const getDefaultValues = (args: {
  scheduleWorker: ScheduleWorker;
  jobsiteContractorsOptions: JobsiteContractorOption[];
  jobsiteWorkersOptions: SelectOptionElement[];
  tradeOptions: SelectOptionElement[];
  activityCodeOptions: SelectOptionElement[];
}): JobsiteScheduledWorkerFormFields => {
  const {
    scheduleWorker,
    jobsiteContractorsOptions,
    jobsiteWorkersOptions,
    tradeOptions,
    activityCodeOptions,
  } = args;
  const {
    jobsiteContractor,
    jobsiteWorker,
    quantity,
    activity,
    trade,
    notes,
    type,
  } = scheduleWorker ?? {};

  const scheduleWorkerTypeOptions = getScheduleWorkerTypeOptions({
    jobsiteWorkersOptions,
    selectedTypeValue: type,
  });

  return {
    jobsiteContractorId:
      jobsiteContractorsOptions.find(
        (option) => option.value === jobsiteContractor?.id,
      ) ?? null,
    jobsiteWorkerId:
      jobsiteWorkersOptions.find(
        (option) => option.value === jobsiteWorker?.jobsiteWorkerId,
      ) ?? null,
    quantity: quantity ?? 1,
    activity:
      activityCodeOptions?.find((option) => option.value === activity) ?? null,
    trade: tradeOptions.find((option) => option.value === trade) ?? null,
    notes: notes ?? '',
    type:
      scheduleWorkerTypeOptions.find((option) => option.value === type) ?? null,
  };
};

export const getAddInput = (
  shiftId: string,
  data: JobsiteScheduledWorkerFormFields,
  dirtyFields: DeepMap<JobsiteScheduledWorkerFormFields, true>,
): CreateScheduleWorkerInput => {
  const getUpdateInputValue = getUpdateInputValueFunction(data, dirtyFields);

  return ensureNonUndefinedFields<CreateScheduleWorkerInput>({
    shiftId,
    jobsiteContractorId: getUpdateInputValue('jobsiteContractorId', true),
    jobsiteWorkerId: getUpdateInputValue('jobsiteWorkerId', true),
    quantity: getAsNumber(getUpdateInputValue('quantity', true)) ?? 1,
    trade: getUpdateInputValue('trade', true),
    activity: getUpdateInputValue('activity', true),
    notes: getUpdateInputValue('notes'),
    type: getUpdateInputValue('type', true),
  });
};

export const getUpdateInput = (
  id: string,
  data: JobsiteScheduledWorkerFormFields,
  dirtyFields: DeepMap<JobsiteScheduledWorkerFormFields, true>,
): UpdateScheduleWorkerInput => {
  const getUpdateInputValue = getUpdateInputValueFunction(data, dirtyFields);

  return ensureNonUndefinedFields<UpdateScheduleWorkerInput>({
    id,
    jobsiteContractorId: getUpdateInputValue('jobsiteContractorId'),
    jobsiteWorkerId: getUpdateInputValue('jobsiteWorkerId'),
    quantity: getAsNumber(getUpdateInputValue('quantity')),
    trade: getUpdateInputValue('trade'),
    activity: getUpdateInputValue('activity'),
    notes: getUpdateInputValue('notes'),
    type: getUpdateInputValue('type'),
  });
};
